/**
 * Regras do mapa do imóvel, separadas do componente para poderem ser testadas
 * sem subir o Leaflet (que precisa de DOM real para medir o contêiner).
 */

export type LatLng = [number, number];

/** Raio, em metros, do círculo que esconde o endereço exato. */
export const DEFAULT_PRIVACY_RADIUS = 300;

/**
 * Limites continentais do Brasil -- os mesmos do geocoding_service no backend.
 * Coordenada zerada ou de outro país não é "quase certa": ela posiciona o
 * imóvel em outro continente, o que é pior do que não mostrar mapa nenhum.
 */
const BRAZIL_BOUNDS = { lat: [-34.0, 5.5], lon: [-74.5, -34.0] };

function asNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Ponto do imóvel, ou null quando não há coordenada confiável para mostrar. */
export function coordinatesOf(address: any): LatLng | null {
  if (!address || typeof address !== 'object') return null;

  const latitude = asNumber(address.latitude);
  const longitude = asNumber(address.longitude);
  if (latitude === null || longitude === null) return null;
  if (latitude === 0 && longitude === 0) return null;

  const [latMin, latMax] = BRAZIL_BOUNDS.lat;
  const [lonMin, lonMax] = BRAZIL_BOUNDS.lon;
  if (latitude < latMin || latitude > latMax) return null;
  if (longitude < lonMin || longitude > lonMax) return null;

  return [latitude, longitude];
}

function normalizeType(type?: string): string {
  return (type || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Desenho do pino por tipo de imóvel (traços do Feather, licença MIT).
 * Fica como string porque o Leaflet monta o marcador com `divIcon`, que recebe
 * HTML e não elemento React.
 */
const TYPE_PATHS: Record<string, string> = {
  casa: '<path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z"/>',
  apartamento:
    '<path d="M5 21V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v17"/><path d="M15 9h3a1 1 0 0 1 1 1v11"/><path d="M8 7h3M8 11h3M8 15h3"/><path d="M3 21h18"/>',
  terreno:
    '<path d="M3 7.5 9 4l6 3.5L21 4v12.5L15 20l-6-3.5L3 20z"/><path d="M9 4v12.5M15 7.5V20"/>',
  comercial:
    '<path d="M3 9.5 4.5 4h15L21 9.5"/><path d="M4 9.5V20a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5"/><path d="M3 9.5a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0"/>',
  chacara:
    '<path d="M12 3a6 6 0 0 1 4 10.5V15H8v-1.5A6 6 0 0 1 12 3z"/><path d="M12 15v6"/><path d="M8 21h8"/>',
  industrial:
    '<path d="M3 21V11l6 4V11l6 4V7l6-4v18z"/><path d="M7 21v-3M13 21v-3M19 21v-3"/>',
};

const FALLBACK_PATH =
  '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>';

export function markerSvgFor(type?: string): string {
  const paths = TYPE_PATHS[normalizeType(type)] || FALLBACK_PATH;
  return (
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
    paths +
    '</svg>'
  );
}

export function privacyRadiusFor(radius?: number): number {
  return typeof radius === 'number' && Number.isFinite(radius) && radius > 0
    ? radius
    : DEFAULT_PRIVACY_RADIUS;
}

interface NoteOptions {
  precision?: string;
  city?: string;
  mode: 'pin' | 'radius';
  radius: number;
}

/**
 * Frase abaixo do mapa. Existe porque o ponto nem sempre tem a mesma precisão:
 * anunciar "precisão de rua" para um imóvel cujo cadastro só tem a cidade seria
 * afirmar uma localização que não temos.
 */
export function locationNote({ precision, city, mode, radius }: NoteOptions): string {
  if (precision === 'city') {
    const onde = city ? `de ${city}` : 'da cidade';
    return `Referência do centro ${onde} — este imóvel não tem rua ou CEP cadastrado.`;
  }
  return mode === 'pin'
    ? 'Localização aproximada, com precisão de rua.'
    : `O imóvel fica dentro desta área de aproximadamente ${radius} metros.`;
}
